import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Block as SuspendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getChefs,
  createChef,
  saveChefAsPending,
  approveChef,
  rejectChef,
  suspendChef,
  Chef,
  CreateChefData,
} from '../adminApi';

type StatusFilter = 'all' | 'pending' | 'approved' | 'active' | 'suspended' | 'rejected';

const ChefList: React.FC = () => {
  const navigate = useNavigate();
  const [chefs, setChefs] = useState<Chef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [selectedChef, setSelectedChef] = useState<Chef | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // New chef form
  const [newChef, setNewChef] = useState<CreateChefData>({
    email: '',
    name: '',
    bio: '',
    website_url: '',
    credentials: '',
    specialties: '',
    cultural_background: '',
    application_notes: '',
    social_links: {
      instagram: '',
      tiktok: '',
      facebook: '',
      twitter: '',
    },
  });

  const resetNewChefForm = () => {
    setNewChef({
      email: '',
      name: '',
      bio: '',
      website_url: '',
      credentials: '',
      specialties: '',
      cultural_background: '',
      application_notes: '',
      social_links: {
        instagram: '',
        tiktok: '',
        facebook: '',
        twitter: '',
      },
    });
  };

  const cleanSocialLinks = (links?: typeof newChef.social_links) => {
    if (!links) return undefined;
    const cleaned: Record<string, string> = {};
    Object.entries(links).forEach(([key, value]) => {
      if (value && value.trim()) {
        cleaned[key] = value.trim();
      }
    });
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  };

  const handleAddChef = async (asPending: boolean) => {
    if (!newChef.email || !newChef.name) {
      setError('Email and name are required');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const dataToSend = {
        ...newChef,
        social_links: cleanSocialLinks(newChef.social_links),
      };

      if (asPending) {
        await saveChefAsPending(dataToSend);
        setSuccessMessage('Chef saved as pending. You can approve or reject them later.');
      } else {
        await createChef(dataToSend);
        setSuccessMessage('Chef created successfully! Welcome email sent.');
      }
      setAddDialogOpen(false);
      resetNewChefForm();
      fetchChefs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create chef');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchChefs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getChefs({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setChefs(result.chefs || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load chefs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchChefs();
  }, [fetchChefs]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusChip = (status: Chef['status']) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      active: 'success',
      approved: 'info',
      pending: 'warning',
      suspended: 'error',
      rejected: 'error',
    };
    return <Chip label={status} size="small" color={colors[status] || 'default'} />;
  };

  const handleApprove = async (chef: Chef) => {
    setActionLoading(true);
    setError(null);
    try {
      await approveChef(chef.chef_id);
      setSuccessMessage(`${chef.name} has been approved!`);
      fetchChefs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve chef');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedChef || !actionReason) return;

    setActionLoading(true);
    setError(null);
    try {
      await rejectChef(selectedChef.chef_id, actionReason);
      setSuccessMessage(`${selectedChef.name} has been rejected.`);
      setRejectDialogOpen(false);
      setSelectedChef(null);
      setActionReason('');
      fetchChefs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject chef');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedChef || !actionReason) return;

    setActionLoading(true);
    setError(null);
    try {
      await suspendChef(selectedChef.chef_id, actionReason);
      setSuccessMessage(`${selectedChef.name} has been suspended.`);
      setSuspendDialogOpen(false);
      setSelectedChef(null);
      setActionReason('');
      fetchChefs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to suspend chef');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Chefs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Chef
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchChefs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Promo Code</TableCell>
              <TableCell>Recipes</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : chefs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  No chefs found
                </TableCell>
              </TableRow>
            ) : (
              chefs.map((chef) => (
                <TableRow
                  key={chef.chef_id}
                  hover
                  onClick={() => navigate(`/admin/chefs/${chef.chef_id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{chef.chef_id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {chef.name}
                    </Typography>
                    {chef.specialties && (
                      <Typography variant="caption" color="text.secondary">
                        {chef.specialties}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{chef.email}</TableCell>
                  <TableCell>{getStatusChip(chef.status)}</TableCell>
                  <TableCell>{chef.slug || '-'}</TableCell>
                  <TableCell>
                    {chef.promo_code ? (
                      <Chip label={chef.promo_code} size="small" variant="outlined" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{chef.recipe_count ?? 0}</TableCell>
                  <TableCell>{formatDate(chef.created_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5}>
                      {chef.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(chef)}
                              disabled={actionLoading}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedChef(chef);
                                setRejectDialogOpen(true);
                              }}
                              disabled={actionLoading}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {(chef.status === 'approved' || chef.status === 'active') && (
                        <Tooltip title="Suspend">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setSelectedChef(chef);
                              setSuspendDialogOpen(true);
                            }}
                            disabled={actionLoading}
                          >
                            <SuspendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Chef Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Chef</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={newChef.email}
              onChange={(e) => setNewChef({ ...newChef, email: e.target.value })}
            />
            <TextField
              label="Name"
              fullWidth
              required
              value={newChef.name}
              onChange={(e) => setNewChef({ ...newChef, name: e.target.value })}
            />
            <TextField
              label="Bio"
              fullWidth
              multiline
              rows={2}
              value={newChef.bio}
              onChange={(e) => setNewChef({ ...newChef, bio: e.target.value })}
            />
            <TextField
              label="Website URL"
              fullWidth
              value={newChef.website_url}
              onChange={(e) => setNewChef({ ...newChef, website_url: e.target.value })}
            />
            <TextField
              label="Credentials"
              fullWidth
              placeholder="e.g., Culinary Institute of America, 10 years experience"
              value={newChef.credentials}
              onChange={(e) => setNewChef({ ...newChef, credentials: e.target.value })}
            />
            <TextField
              label="Specialties"
              fullWidth
              placeholder="e.g., Italian, Vegan, Pastry"
              value={newChef.specialties}
              onChange={(e) => setNewChef({ ...newChef, specialties: e.target.value })}
            />
            <TextField
              label="Cultural Background"
              fullWidth
              value={newChef.cultural_background}
              onChange={(e) => setNewChef({ ...newChef, cultural_background: e.target.value })}
            />
            <TextField
              label="Internal Notes"
              fullWidth
              multiline
              rows={2}
              value={newChef.application_notes}
              onChange={(e) => setNewChef({ ...newChef, application_notes: e.target.value })}
            />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Social Media (optional)
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Instagram"
                size="small"
                placeholder="@username"
                value={newChef.social_links?.instagram || ''}
                onChange={(e) => setNewChef({
                  ...newChef,
                  social_links: { ...newChef.social_links, instagram: e.target.value }
                })}
              />
              <TextField
                label="TikTok"
                size="small"
                placeholder="@username"
                value={newChef.social_links?.tiktok || ''}
                onChange={(e) => setNewChef({
                  ...newChef,
                  social_links: { ...newChef.social_links, tiktok: e.target.value }
                })}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Facebook"
                size="small"
                placeholder="username or page"
                value={newChef.social_links?.facebook || ''}
                onChange={(e) => setNewChef({
                  ...newChef,
                  social_links: { ...newChef.social_links, facebook: e.target.value }
                })}
              />
              <TextField
                label="X (Twitter)"
                size="small"
                placeholder="@username"
                value={newChef.social_links?.twitter || ''}
                onChange={(e) => setNewChef({
                  ...newChef,
                  social_links: { ...newChef.social_links, twitter: e.target.value }
                })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => handleAddChef(true)}
            disabled={actionLoading || !newChef.email || !newChef.name}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Save as Pending'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleAddChef(false)}
            disabled={actionLoading || !newChef.email || !newChef.name}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Create & Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Chef Application</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{selectedChef?.name}</strong>'s application?
          </Typography>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Explain why this application is being rejected..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setActionReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend Chef</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to suspend <strong>{selectedChef?.name}</strong>? This will deactivate their promo code.
          </Typography>
          <TextField
            label="Suspension Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Explain why this chef is being suspended..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSuspendDialogOpen(false);
            setActionReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuspend}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Suspend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChefList;
